import { atom, map } from "nanostores"

import type sdk from "@/api/sdk"
import { useStore } from "@nanostores/react"
import { type AppleAuthenticationCredential } from "expo-apple-authentication/src/AppleAuthentication.types"

import { type GoogleCode } from "@/components/buttons/login/GoogleLoginButton"

type RegistrationInputs = Parameters<typeof sdk.Registration>[0]
type RegistrationInputErrors = Nullable<Parameters<typeof sdk.Registration>[0]>

export type ConfirmationModeType = "email" | "google" | "apple" | "initial"
const $confirmationMode = atom<ConfirmationModeType>("initial")

const setConfirmationMode = (mode: ConfirmationModeType) => {
  $confirmationMode.set(mode)
}

const $googleCode = atom<GoogleCode | null>(null)

export const setGoogleCode = (code: GoogleCode | null) => {
  $googleCode.set(code)
}

const $appleCode = atom<AppleAuthenticationCredential | null>(null)

export const setAppleCode = (code: AppleAuthenticationCredential | null) => {
  $appleCode.set(code)
}

const $confirmAgreeToTerms = atom<boolean>(false)
const $confirmAgreeToTermsError = atom<string | null>(null)

const setConfirmAgreeToTerms = (value: boolean) => {
  $confirmAgreeToTerms.set(value)
  if (value) {
    setConfirmAgreeToTermsError(null)
  }
}

const setConfirmAgreeToTermsError = (value: string | null) => {
  $confirmAgreeToTermsError.set(value)
}

const validateConfirmAgreeToTerms = () => {
  const isConfirmed = $confirmAgreeToTerms.get()
  if (!isConfirmed) {
    setConfirmAgreeToTermsError("Please check the box to continue.")
    return false
  }
  setConfirmAgreeToTermsError(null)
  return true
}

const defaultInputs = () => {
  return {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phoneNumber: "",
  }
}

const defaultErrors = () => {
  return {
    firstName: null,
    lastName: null,
    email: null,
    password: null,
    phoneNumber: null,
  }
}

const $registerInputs = map<RegistrationInputs>(defaultInputs())

const $registerInputErrors = map<RegistrationInputErrors>(defaultErrors())

export const setFirstName = (firstName: string | null | undefined) => {
  $registerInputs.setKey("firstName", firstName || "")
}

export const setFirstNameError = (error: string | null) => {
  $registerInputErrors.setKey("firstName", error)
}

const validateFirstName = () => {
  const firstName = $registerInputs.get().firstName
  if (!firstName || (firstName && firstName.length === 0)) {
    setFirstNameError("First name is required.")
    return false
  }

  setFirstNameError(null)
  return true
}

export const setLastName = (lastName: string | null | undefined) => {
  $registerInputs.setKey("lastName", lastName || "")
}

export const setLastNameError = (error: string | null) => {
  $registerInputErrors.setKey("lastName", error)
}

const validateLastName = () => {
  const lastName = $registerInputs.get().lastName
  if (!lastName || (lastName && lastName.length === 0)) {
    setLastNameError("Last name is required.")
    return false
  }

  setLastNameError(null)
  return true
}

export const setEmail = (email: string | null | undefined) => {
  $registerInputs.setKey("email", email || "")
}

export const setEmailError = (error: string | null) => {
  $registerInputErrors.setKey("email", error)
}

const validateEmail = () => {
  const email = $registerInputs.get().email
  if (!email || (email && email.length === 0)) {
    setEmailError("Email address is required.")
    return false
  }

  if (email && (!email.includes("@") || email.trim().endsWith("con"))) {
    setEmailError("Invalid email address")
    return false
  }

  setEmailError(null)
  return true
}

export const setPhoneNumber = (val: string | null | undefined) => {
  if (!val || val.length === 0 || (val.length === 1 && val.charAt(0) === "(")) {
    $registerInputs.setKey("phoneNumber", "")
    return
  }

  val = val.replace(/\D/g, "")
  if (val.length <= 3) {
    $registerInputs.setKey("phoneNumber", `(${val}`)
    return
  }
  if (val.length <= 6) {
    $registerInputs.setKey(
      "phoneNumber",
      `(${val.substring(0, 3)}) ${val.substring(3)}`
    )
    return
  }
  if (val.length > 10) {
    val = val.substring(0, 10)
  }
  $registerInputs.setKey(
    "phoneNumber",
    `(${val.substring(0, 3)}) ${val.substring(3, 6)}-${val.substring(6)}`
  )
}

export const setPhoneNumberError = (error: string | null) => {
  $registerInputErrors.setKey("phoneNumber", error)
}

const validatePhoneNumber = () => {
  const phoneNumber = $registerInputs.get().phoneNumber
  if (phoneNumber) {
    const digits = phoneNumber?.replace(/\D/g, "")
    if (digits.length > 0 && digits.length !== 10) {
      setPhoneNumberError("Invalid phone number")
      return false
    }
  }
  setPhoneNumberError(null)
  return true
}

export const setPassword = (password: string | null | undefined) => {
  $registerInputs.setKey("password", password || "")
}

export const setPasswordError = (error: string | null) => {
  $registerInputErrors.setKey("password", error)
}

const validatePassword = () => {
  const password = $registerInputs.get().password
  if (!password || (password && password.length === 0)) {
    setPasswordError("Password is required.")
    return false
  }

  if (password && password.length < 7) {
    setPasswordError("Invalid password. Minimum of 7 characters.")
    return false
  }

  setPasswordError(null)
  return true
}

export const setRefCode = (refCode: string) => {
  $registerInputs.setKey("refCode", refCode)
}

const runValidations = (validations: Array<() => boolean>) => {
  return validations.reduce((isCurrentlyValid, validate) => {
    const isValid = validate()
    return isCurrentlyValid && isValid
  }, true)
}

export const validateEmailSignUpInputs = () => {
  return runValidations([validateEmail, validatePassword])
}

export const validateConfimSignUpInputs = () => {
  return runValidations([
    validateFirstName,
    validateLastName,
    validatePhoneNumber,
    validateConfirmAgreeToTerms,
  ])
}

const reset = () => {
  $registerInputs.set(defaultInputs())
  $confirmationMode.set("initial")
  $registerInputErrors.set(defaultErrors())
  $confirmAgreeToTerms.set(false)
  $confirmAgreeToTermsError.set(null)
}

export const useRegistrationInputs = () => {
  const registerInputs = useStore($registerInputs)
  const inputErrors = useStore($registerInputErrors)

  const confirmationMode = useStore($confirmationMode)
  const confirmAgreeToTerms = useStore($confirmAgreeToTerms)
  const confirmAgreeToTermsError = useStore($confirmAgreeToTermsError)

  const googleCode = useStore($googleCode)
  const appleCode = useStore($appleCode)

  return {
    registerInputs,
    inputErrors,
    setFirstName,
    setFirstNameError,
    setLastName,
    setLastNameError,
    setEmail,
    setEmailError,
    setPhoneNumber,
    setPhoneNumberError,
    setPassword,
    setPasswordError,
    setRefCode,
    validateEmailSignUpInputs,
    validateConfimSignUpInputs,
    confirmationMode,
    setConfirmationMode,
    confirmAgreeToTerms,
    setConfirmAgreeToTerms,
    confirmAgreeToTermsError,
    setConfirmAgreeToTermsError,
    googleCode,
    setGoogleCode,
    appleCode,
    setAppleCode,
    reset,
  }
}
